/**
 * ShiftMinimal — Global Motion System v2
 *
 * Active systems:
 *   1. Magnetic Button         — cursor-pull on [data-magnetic] (floating dock)
 *   2. Product Grid Tilt       — cursor tilt on [data-pg-card] desktop
 *   3. Stamp Reveal            — badge scale-in on [data-sm-stamp]
 *   4. Animation Budget        — pause marquees while hero fills viewport
 *   5. PDP Product Sizing      — responsive sizing inside glass cards
 */

(function () {
  'use strict';

  /* ── 1. MAGNETIC BUTTON ──────────────────────────────────────────────────
     [data-magnetic] elements are pulled toward the cursor within 110px.
     Spring lerp at 14% per frame for smooth return. Touch devices skipped.
  ─────────────────────────────────────────────────────────────────────── */
  function initMagneticButtons() {
    if (window.matchMedia('(pointer:coarse)').matches) return;
    const btns = Array.from(document.querySelectorAll('[data-magnetic]:not([data-magnetic-bound])'));
    if (!btns.length) return;

    btns.forEach(btn => btn.setAttribute('data-magnetic-bound', ''));

    const states = btns.map(btn => ({ btn, tx: 0, ty: 0, cx: 0, cy: 0 }));

    function lerp(a, b, t) { return a + (b - a) * t; }

    if (!window.__smMagneticMove) {
      window.__smMagneticMove = function (e) {
        const all = window.__smMagneticStates || [];
        all.forEach(s => {
          const r  = s.btn.getBoundingClientRect();
          const bx = r.left + r.width  / 2;
          const by = r.top  + r.height / 2;
          const dx = e.clientX - bx;
          const dy = e.clientY - by;
          const dist  = Math.sqrt(dx * dx + dy * dy);
          const range = 110;
          if (dist < range) {
            const pull = (1 - dist / range) * 0.42;
            s.tx = dx * pull;
            s.ty = dy * pull;
          } else {
            s.tx = 0;
            s.ty = 0;
          }
        });
        if (!window.__smMagneticRaf) {
          window.__smMagneticRaf = requestAnimationFrame(function smMagTick() {
            const list = window.__smMagneticStates || [];
            let active = false;
            list.forEach(s => {
              s.cx = lerp(s.cx, s.tx, 0.14);
              s.cy = lerp(s.cy, s.ty, 0.14);
              s.btn.style.transform = `translate(${s.cx.toFixed(2)}px, ${s.cy.toFixed(2)}px)`;
              if (Math.abs(s.cx - s.tx) > 0.05 || Math.abs(s.cy - s.ty) > 0.05) active = true;
            });
            window.__smMagneticRaf = active ? requestAnimationFrame(smMagTick) : null;
          });
        }
      };
      window.addEventListener('mousemove', window.__smMagneticMove, { passive: true });
    }

    window.__smMagneticStates = (window.__smMagneticStates || []).concat(states);
  }

  /* ── 2. PRODUCT GRID TILT ───────────────────────────────────────────────
     [data-pg-card] shirt layer tracks cursor on desktop (pointer:fine).
     Tilt on .sm-pg__zoom-clip; Z-lift on .sm-pg__img. Plate carries depth shadow.
     Coords relative to card center; angles clamped ±12deg; Z 30–50px.
  ─────────────────────────────────────────────────────────────────────── */
  function initProductGridTilt() {
    if (window.matchMedia('(pointer: coarse)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    function clamp(val, min, max) {
      return Math.max(min, Math.min(max, val));
    }

    const cards = Array.from(document.querySelectorAll('[data-pg-card]:not([data-pg-tilt-bound])'));
    if (!cards.length && !window.__smPgTiltBound) return;

    cards.forEach(function (card) {
      card.setAttribute('data-pg-tilt-bound', '');
      const clip = card.querySelector('.sm-pg__zoom-clip');
      if (!card.querySelector('.sm-pg__img') || !clip || card.classList.contains('sm-pg__card--sold-out')) return;

      card.addEventListener('mouseenter', function () {
        const img = card.querySelector('.sm-pg__img:not([hidden])') || card.querySelector('.sm-pg__img');
        if (!img) return;
        window.__smPgTiltActive = { card: card, img: img, clip: clip, cx: 0, cy: 0 };
        img.classList.add('sm-pg__img--live');
        clip.classList.add('sm-pg__zoom-clip--live');
      });

      card.addEventListener('mouseleave', function () {
        if (window.__smPgTiltActive && window.__smPgTiltActive.card === card) {
          window.__smPgTiltActive = null;
        }
        card.querySelectorAll('.sm-pg__img').forEach(function (image) {
          image.style.removeProperty('--pg-img-z');
          image.classList.remove('sm-pg__img--live');
        });
        clip.style.removeProperty('--pg-z');
        clip.style.removeProperty('--pg-tilt-x');
        clip.style.removeProperty('--pg-tilt-y');
        clip.classList.remove('sm-pg__zoom-clip--live');
      });
    });

    if (window.__smPgTiltBound) return;
    window.__smPgTiltBound = true;

    window.__smPgTiltActive = null;

    window.addEventListener('mousemove', function (e) {
      const active = window.__smPgTiltActive;
      if (!active) return;

      const r = active.card.getBoundingClientRect();
      if (!r.width || !r.height) return;

      active.cx = clamp((e.clientX - (r.left + r.width / 2)) / (r.width / 2), -1, 1);
      active.cy = clamp((e.clientY - (r.top + r.height / 2)) / (r.height / 2), -1, 1);

      if (window.__smPgTiltRaf) return;
      window.__smPgTiltRaf = requestAnimationFrame(function () {
        window.__smPgTiltRaf = null;
        const a = window.__smPgTiltActive;
        if (!a) return;

        var tiltY = clamp(a.cx * 12, -12, 12);
        var tiltX = clamp(-a.cy * 12, -12, 12);
        var dist = Math.abs(a.cx) + Math.abs(a.cy);
        var zBoost = clamp(30 + dist * 10, 30, 50);

        a.clip.style.setProperty('--pg-tilt-y', tiltY.toFixed(1) + 'deg');
        a.clip.style.setProperty('--pg-tilt-x', tiltX.toFixed(1) + 'deg');
        a.clip.style.setProperty('--pg-z', zBoost.toFixed(0) + 'px');
        a.img.style.setProperty('--pg-img-z', (10 + zBoost * 0.25).toFixed(0) + 'px');
      });
    }, { passive: true });
  }

  /* ── Card gallery preload threshold: rootMargin px before viewport ── */
  var PG_GALLERY_PRELOAD_MARGIN = 200;

  /* Product-card gallery: back is slide 1/default, then front.
     Controls are real buttons layered above the product links. Each interaction
     still stops propagation so gallery input never triggers navigation.
     Chosen slide stays until the customer changes it — no idle/scroll/leave reset. */
  function initProductCardGalleries() {
    var cards = Array.from(document.querySelectorAll('[data-pg-card]:not([data-pg-gallery-bound])'));
    if (!cards.length) return;

    /* Shared preload observer: one instance watches all cards to limit browser work. */
    if (!window.__smPgGalleryPreloadObserver) {
      window.__smPgGalleryPreloadObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var card = entry.target;
          if (!card.hasAttribute('data-pg-gallery-bound')) return;
          card.querySelectorAll('[data-pg-image][hidden]').forEach(function (image) {
            if (!image.complete || image.naturalWidth === 0) {
              image.loading = 'eager';
              if (typeof image.decode === 'function') {
                image.decode().catch(function () { /* noop */ });
              }
            }
          });
          window.__smPgGalleryPreloadObserver.unobserve(card);
        });
      }, { rootMargin: PG_GALLERY_PRELOAD_MARGIN + 'px' });
    }

    cards.forEach(function (card) {
      var images = Array.from(card.querySelectorAll('[data-pg-image]'));
      var previous = card.querySelector('[data-pg-gallery-prev]');
      var next = card.querySelector('[data-pg-gallery-next]');
      var current = card.querySelector('[data-pg-gallery-current]');
      var viewLabel = card.querySelector('[data-pg-gallery-view]');
      if (images.length <= 1 || !previous || !next) return;

      card.setAttribute('data-pg-gallery-bound', '1');
      var persisted = parseInt(card.getAttribute('data-pg-active') || '', 10);
      var index = Number.isFinite(persisted)
        ? Math.max(0, Math.min(images.length - 1, persisted))
        : Math.max(0, images.findIndex(function (image) { return !image.hidden; }));
      if (index < 0) index = 0;

      /* Register this card with the shared preload observer. */
      window.__smPgGalleryPreloadObserver.observe(card);

      /* Track in-flight switch so rapid clicks don't race. */
      var switching = false;
      /* Block ghost click after a swipe so front→back doesn't double-toggle. */
      var suppressClickUntil = 0;

      function show(nextIndex) {
        if (switching) return;
        var outgoing = images[index];
        var incomingIndex = (nextIndex + images.length) % images.length;
        var incoming = images[incomingIndex];

        if (incoming.complete && incoming.naturalWidth > 0) {
          /* Already decoded — swap immediately. */
          applyShow(incomingIndex, outgoing);
        } else {
          /* Image hasn't been decoded yet. Force load + decode first.
             Keep outgoing visible; show incoming only after decode. */
          switching = true;
          incoming.loading = 'eager';
          var decodePromise = typeof incoming.decode === 'function'
            ? incoming.decode()
            : Promise.resolve();
          decodePromise.then(function () {
            applyShow(incomingIndex, outgoing);
            switching = false;
          }).catch(function () {
            /* Fall through — show anyway if decode fails. */
            applyShow(incomingIndex, outgoing);
            switching = false;
          });
        }
      }

      function applyShow(newIndex, outgoing) {
        index = newIndex;
        card.setAttribute('data-pg-active', String(index));

        images.forEach(function (image, imageIndex) {
          var active = imageIndex === index;
          image.hidden = !active;
          image.classList.toggle('is-active', active);
          image.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
        if (current) current.textContent = String(index + 1);
        if (viewLabel) viewLabel.textContent = index === 0 ? 'BACK' : 'FRONT';

        var activeTilt = window.__smPgTiltActive;
        if (activeTilt && activeTilt.card === card) {
          if (outgoing) {
            outgoing.classList.remove('sm-pg__img--live');
            outgoing.style.removeProperty('--pg-img-z');
          }
          activeTilt.img = images[index];
          activeTilt.img.classList.add('sm-pg__img--live');
        }
      }

      /* Sync initial/persisted slide into the DOM once. */
      applyShow(index, null);

      function activate(delta, event) {
        if (event) {
          if (Date.now() < suppressClickUntil) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          event.preventDefault();
          event.stopPropagation();
        }
        show(index + delta);
      }

      previous.addEventListener('click', function (event) { activate(-1, event); });
      next.addEventListener('click', function (event) { activate(1, event); });

      var touchStartX = null;
      var touchStartY = null;
      var touchOnArrow = false;
      card.addEventListener('touchstart', function (event) {
        if (event.touches.length !== 1) return;
        var target = event.target;
        touchOnArrow = !!(target && target.closest && target.closest('[data-pg-gallery-prev], [data-pg-gallery-next]'));
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
      }, { passive: true });
      card.addEventListener('touchend', function (event) {
        if (touchStartX === null || event.changedTouches.length !== 1) return;
        var deltaX = event.changedTouches[0].clientX - touchStartX;
        var deltaY = event.changedTouches[0].clientY - touchStartY;
        touchStartX = null;
        touchStartY = null;
        /* Arrow taps use the click handler only — avoids swipe+click double flip. */
        if (touchOnArrow) {
          touchOnArrow = false;
          return;
        }
        touchOnArrow = false;
        if (Math.abs(deltaX) < 44 || Math.abs(deltaX) < Math.abs(deltaY) * 1.15) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClickUntil = Date.now() + 450;
        show(index + (deltaX < 0 ? 1 : -1));
      });
    });
  }

  /* ── 3. STAMP REVEAL — NEW / drop badges scale in once on IO ── */
  function initStampReveal() {
    const stamps = Array.from(document.querySelectorAll('[data-sm-stamp]:not([data-stamp-done])'));
    if (!stamps.length) return;

    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        el.classList.add('sm-stamp--in');
        el.setAttribute('data-stamp-done', '');
        io.unobserve(el);
      });
    }, { threshold: 0.35, rootMargin: '0px 0px -8% 0px' });

    stamps.forEach(function (el) { io.observe(el); });
  }

  /* Marquee pause while hero fills viewport — removed: data-hero-priority
     paused visible marquees on homepage. Off-screen IO in marquee.liquid is enough. */
  function initAnimationBudget() {
    document.querySelectorAll('marquee-component[data-hero-priority]').forEach(function (mc) {
      mc.removeAttribute('data-hero-priority');
    });
  }

  /* ── 5. DROP ARTIFACT PDP — Responsive product sizing ──
     Card dimensions remain controlled by CSS. JS reads painted card
     dimensions and exposes safe maximum dimensions for the product image.
  ─────────────────────────────────────────────────────────────────────── */
  function measureFloatCard(media) {
    var cardEl = media.querySelector('.sm-float-card');
    if (!cardEl) return;

    var cardRect = cardEl.getBoundingClientRect();
    var cardW = cardRect.width;
    var cardH = cardRect.height;
    if (cardW < 1 || cardH < 1) return;

    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var safePad = isMobile ? 10 : 16;
    var safeWidth = Math.max(0, cardW - safePad * 2);
    var safeHeight = Math.max(0, cardH - safePad * 2);
    var viewportWidth = Math.round(window.innerWidth || document.documentElement.clientWidth || 0);
    var previousViewportWidth = Number(media.getAttribute('data-sm-da-vw') || 0);
    var previousSafeWidth = parseFloat(media.style.getPropertyValue('--sm-shirt-max-w'));
    var previousSafeHeight = parseFloat(media.style.getPropertyValue('--sm-shirt-max-h'));

    /* Variant swaps can briefly report the slideshow/card while Shopify is
       hiding or rebuilding media. If we accept that temporary smaller number,
       the tee visibly shrinks after S -> M even though the glass card itself
       did not change. Keep the largest stable cap for the same viewport; real
       responsive changes still update after the viewport width changes. */
    if (previousViewportWidth === viewportWidth && Number.isFinite(previousSafeWidth)) {
      safeWidth = Math.max(safeWidth, previousSafeWidth);
    }
    if (previousViewportWidth === viewportWidth && Number.isFinite(previousSafeHeight)) {
      safeHeight = Math.max(safeHeight, previousSafeHeight);
    }

    media.setAttribute('data-sm-da-vw', String(viewportWidth));
    media.style.setProperty('--sm-shirt-max-w', safeWidth.toFixed(1) + 'px');
    media.style.setProperty('--sm-shirt-max-h', safeHeight.toFixed(1) + 'px');
  }

  function initDropArtifactSizing() {
    var mediaList = document.querySelectorAll('.sm-drop-artifact .product-information__media:not([data-sm-da-bound])');
    if (!mediaList.length) return;

    mediaList.forEach(function (media) {
      media.setAttribute('data-sm-da-bound', '');

      var pending = null;
      var run = function () {
        if (pending) return;
        pending = window.requestAnimationFrame(function () {
          pending = null;
          measureFloatCard(media);
        });
      };

      run();

      if (typeof ResizeObserver !== 'undefined') {
        var resizeObserver = new ResizeObserver(function () { run(); });
        resizeObserver.observe(media);

        var card = media.querySelector('.sm-float-card');
        if (card) resizeObserver.observe(card);

        var gallery = media.querySelector('media-gallery, .media-gallery, slideshow-component');
        if (gallery) resizeObserver.observe(gallery);
      } else {
        window.addEventListener('resize', run, { passive: true });
      }

      var slideshow = media.querySelector('slideshow-component, slideshow-slides');
      if (slideshow && typeof MutationObserver !== 'undefined') {
        var mutationObserver = new MutationObserver(function () { run(); });
        mutationObserver.observe(slideshow, {
          attributes: true,
          subtree: true,
          childList: true,
          attributeFilter: ['aria-hidden', 'class', 'style']
        });
      }

      media.querySelectorAll('img').forEach(function (image) {
        if (image.complete) return;
        image.addEventListener('load', run, { once: true });
      });
    });
  }

  /* Keep transparent T-shirt artwork centered inside PDP glass cards. The
     Horizon slideshow defaults every nested layer to flex-end; apply the
     centering invariant once at setup, never during animation or scrolling. */
  function initDropArtifactCentering() {
    var sceneRoot = '.sm-drop-artifact .sm-float-card__scene';
    var centeringLayers = [
      sceneRoot,
      sceneRoot + ' media-gallery',
      sceneRoot + ' slideshow-component',
      sceneRoot + ' slideshow-container',
      sceneRoot + ' slideshow-slides',
      sceneRoot + ' slideshow-slide',
      sceneRoot + ' .product-media'
    ].join(',');
    var shirtImages = [
      sceneRoot + ' img.product-media__image',
      sceneRoot + ' .product-media-container img'
    ].join(',');

    document.querySelectorAll(centeringLayers).forEach(function (element) {
      element.style.setProperty('align-items', 'center', 'important');

      /* The slide track is a horizontal strip, not a single artwork layer.
         Centering the strip places slide 2 in the viewport while Horizon still
         reports slide 1. Start-align the strip; each slide centers its own art. */
      if (element.matches('slideshow-slides')) {
        /* Scrollable for Horizon select(); touch-action blocks accidental pan-x. */
        element.style.setProperty('overflow-x', 'auto', 'important');
        element.style.setProperty('overflow-y', 'hidden', 'important');
        element.style.setProperty('touch-action', 'pan-y pinch-zoom', 'important');
        element.style.setProperty('scroll-snap-type', 'x mandatory', 'important');
      } else if (element.matches('slideshow-component, slideshow-container')) {
        /* Clip siblings so resting states never show a sleeve peek. */
        element.style.setProperty('overflow', 'hidden', 'important');
        element.style.setProperty('overflow-x', 'hidden', 'important');
        element.style.setProperty('overflow-y', 'hidden', 'important');
      }
      element.style.setProperty(
        'justify-content',
        element.matches('slideshow-slides') ? 'flex-start' : 'center',
        'important'
      );
    });

    document.querySelectorAll(sceneRoot + ' slideshow-slide').forEach(function (slide) {
      slide.style.setProperty('flex', '0 0 100%', 'important');
      slide.style.setProperty('width', '100%', 'important');
      slide.style.setProperty('scroll-snap-align', 'start', 'important');
      slide.style.setProperty('scroll-snap-stop', 'always', 'important');
    });

    document.querySelectorAll(shirtImages).forEach(function (image) {
      image.style.setProperty('object-position', '50% 50%', 'important');
      image.style.setProperty('align-self', 'center', 'important');
      image.style.setProperty('margin-block', 'auto', 'important');
      if (!image.hasAttribute('data-sm-center-load-bound')) {
        image.setAttribute('data-sm-center-load-bound', '');
        image.addEventListener('load', scheduleDropArtifactCentering, { passive: true });
      }
    });

    document.querySelectorAll(sceneRoot + ':not([data-sm-center-bound])').forEach(function (scene) {
      scene.setAttribute('data-sm-center-bound', '');
      if (typeof MutationObserver !== 'undefined') {
        var observer = new MutationObserver(scheduleDropArtifactCentering);
        observer.observe(scene, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['aria-hidden', 'class', 'src', 'srcset']
        });
      }
    });
  }

  function scheduleDropArtifactCentering() {
    if (window.__smDropCenterRaf) return;
    window.__smDropCenterRaf = window.requestAnimationFrame(function () {
      window.__smDropCenterRaf = null;
      initDropArtifactCentering();
      window.setTimeout(initDropArtifactCentering, 120);
    });
  }

  /* Keep the PDP shirt view in lockstep with Horizon's slideshow counter.
     Resting state must always be one full slide — never a half-scroll peek.
     Intentional swipe/arrows change slides; vertical page scroll cannot leave
     the track mid-slide. Scoped to the product artifact card only. */
  function initDropArtifactGalleryNavigation() {
    document.querySelectorAll('.sm-drop-artifact .sm-float-card__scene:not([data-sm-gallery-nav-bound])').forEach(function (scene) {
      var track = scene.querySelector('slideshow-slides');
      if (!track) return;

      var slides = Array.from(track.querySelectorAll('slideshow-slide'));
      if (slides.length < 2) return;

      scene.setAttribute('data-sm-gallery-nav-bound', '');

      var slideshow = scene.querySelector('slideshow-component');
      var startX = 0;
      var startY = 0;
      var lockedIndex = 0;
      var axisLock = null; /* 'x' | 'y' | null */
      var intentionalNav = false;
      var settleTimer = null;
      var navigationTimer = null;
      var AXIS_THRESHOLD = 10;
      var SWIPE_DISTANCE = 48;

      function slideOffset(index) {
        var slide = slides[Math.max(0, Math.min(slides.length - 1, index))];
        return slide ? slide.offsetLeft : 0;
      }

      function currentIndex() {
        var width = track.clientWidth || 1;
        var raw = track.scrollLeft / width;
        return Math.max(0, Math.min(slides.length - 1, Math.round(raw)));
      }

      function finishNavigation(delay) {
        window.clearTimeout(navigationTimer);
        navigationTimer = window.setTimeout(function () {
          var target = slideOffset(lockedIndex);
          if (Math.abs(track.scrollLeft - target) > 1) {
            track.scrollTo({ left: target, behavior: 'auto' });
          }
          intentionalNav = false;
        }, delay);
      }

      function lockTo(index, instant) {
        var next = Math.max(0, Math.min(slides.length - 1, index));
        lockedIndex = next;
        intentionalNav = true;
        window.clearTimeout(settleTimer);
        window.clearTimeout(navigationTimer);

        var left = slideOffset(next);
        var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        var behavior = instant || reduce ? 'auto' : 'smooth';

        if (slideshow && typeof slideshow.select === 'function') {
          try {
            slideshow.select(next, undefined, { animate: !instant && !reduce });
          } catch (error) {
            track.scrollTo({ left: left, behavior: behavior });
          }
        } else {
          track.scrollTo({ left: left, behavior: behavior });
        }

        /* Hard-correct after animation so a cancelled/interrupted select
           cannot leave a sleeve peeking at the edge. */
        finishNavigation(instant || reduce ? 60 : 480);
      }

      function snapToNearest(instant) {
        if (intentionalNav) return;
        lockTo(currentIndex(), instant !== false);
      }

      /* Seed locked index from the painted position once. */
      lockedIndex = currentIndex();
      if (Math.abs(track.scrollLeft - slideOffset(lockedIndex)) > 1) {
        track.scrollTo({ left: slideOffset(lockedIndex), behavior: 'auto' });
      }

      /* Horizon already owns the arrow buttons. Observe its selection so the
         vertical-scroll guard stays dormant until the native motion settles. */
      if (slideshow) {
        slideshow.addEventListener('slideshow:select', function (event) {
          var detail = event.detail;
          if (!detail || detail.trigger !== 'select' || !detail.userInitiated) return;

          lockedIndex = Math.max(0, Math.min(slides.length - 1, detail.index));
          intentionalNav = true;
          window.clearTimeout(settleTimer);
          finishNavigation(window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 60 : 480);
        });
      }

      function hardLockVertical() {
        if (intentionalNav || axisLock === 'x') return;
        var target = slideOffset(lockedIndex);
        if (Math.abs(track.scrollLeft - target) > 0.5) {
          track.scrollLeft = target;
        }
      }

      track.addEventListener('scroll', function () {
        if (intentionalNav) return;
        if (axisLock === 'y') {
          hardLockVertical();
          return;
        }
        /* After any free scroll ends, snap to one full slide. */
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(function () {
          if (!intentionalNav) snapToNearest(true);
        }, 90);
      }, { passive: true });

      if ('onscrollend' in window) {
        track.addEventListener('scrollend', function () {
          if (!intentionalNav && axisLock !== 'y') snapToNearest(true);
        }, { passive: true });
      }

      track.addEventListener('touchstart', function (event) {
        if (event.touches.length !== 1) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        lockedIndex = currentIndex();
        axisLock = null;
      }, { passive: true });

      track.addEventListener('touchmove', function (event) {
        if (event.touches.length !== 1) return;
        var dx = event.touches[0].clientX - startX;
        var dy = event.touches[0].clientY - startY;

        if (axisLock === null && (Math.abs(dx) > AXIS_THRESHOLD || Math.abs(dy) > AXIS_THRESHOLD)) {
          axisLock = Math.abs(dx) > Math.abs(dy) * 1.35 ? 'x' : 'y';
        }

        if (axisLock === 'y') {
          hardLockVertical();
        }
      }, { passive: true });

      track.addEventListener('touchend', function (event) {
        var wasHorizontal = axisLock === 'x';
        var wasVertical = axisLock === 'y';
        axisLock = null;

        if (wasVertical) {
          hardLockVertical();
          return;
        }

        if (!wasHorizontal || event.changedTouches.length !== 1) {
          snapToNearest(true);
          return;
        }

        var dx = event.changedTouches[0].clientX - startX;
        if (Math.abs(dx) < SWIPE_DISTANCE) {
          /* Incomplete swipe — return to the slide they started on. */
          lockTo(lockedIndex, true);
          return;
        }

        lockTo(lockedIndex + (dx < 0 ? 1 : -1), false);
      }, { passive: true });

      track.addEventListener('touchcancel', function () {
        axisLock = null;
        lockTo(lockedIndex, true);
      }, { passive: true });

      /* Wheel / trackpad: intentional horizontal → change slide; vertical left alone. */
      track.addEventListener('wheel', function (event) {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY) && Math.abs(event.deltaX) > 2) {
          event.preventDefault();
          lockTo(lockedIndex + (event.deltaX > 0 ? 1 : -1), true);
        } else {
          hardLockVertical();
        }
      }, { passive: false });
    });
  }

  /* ── Init ───────────────────────────────────────────────────────────── */
  function init() {
    initMagneticButtons();
    initProductCardGalleries();
    initProductGridTilt();
    initStampReveal();
    initAnimationBudget();
    initDropArtifactSizing();
    initDropArtifactCentering();
    /* Horizon owns product-gallery arrows, dragging, and snap state. The CSS
       already enforces pan-y plus mandatory x snapping, so a second JS
       navigation controller only creates competing scroll corrections. */
    initRealmOpening();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Lightweight homepage-to-collection "realm opening" transition.
     Scoped to normal left-clicks from the homepage to /collections/*.
     Reduced motion, new tabs, downloads, external links, and modified clicks
     keep native navigation. No canvas, WebGL, image processing, or filters. */
  function shouldReduceRealmMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isHomepagePath(pathname) {
    return pathname === '/' || pathname === '';
  }

  function buildRealmOverlay(mode) {
    var overlay = document.createElement('div');
    overlay.className = 'sm-realm-ov sm-realm-ov--' + mode;
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML = '<span class="sm-realm-ov__aura"></span><span class="sm-realm-ov__disc"></span><span class="sm-realm-ov__rift"></span>';
    return overlay;
  }

  function playRealmEntry() {
    try {
      if (sessionStorage.getItem('smRealmOpening') !== '1') return;
      sessionStorage.removeItem('smRealmOpening');
    } catch (error) {
      return;
    }

    if (shouldReduceRealmMotion()) return;

    var overlay = buildRealmOverlay('entry');
    document.body.appendChild(overlay);
    window.setTimeout(function () {
      overlay.remove();
    }, 520);
  }

  function playRealmExit(url) {
    if (window.__smRealmOpeningActive) return;
    window.__smRealmOpeningActive = true;

    var overlay = buildRealmOverlay('exit');
    document.body.appendChild(overlay);

    window.setTimeout(function () {
      window.location.href = url;
    }, 430);
  }

  function initRealmOpening() {
    playRealmEntry();

    if (window.__smRealmOpeningBound) return;
    window.__smRealmOpeningBound = true;

    document.addEventListener('click', function (event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!isHomepagePath(window.location.pathname)) return;
      if (shouldReduceRealmMotion()) return;

      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var link = target.closest('a[href]');
      if (!link || link.hasAttribute('download')) return;
      if (link.target && link.target.toLowerCase() !== '_self') return;

      var destination;
      try {
        destination = new URL(link.href, window.location.href);
      } catch (error) {
        return;
      }

      if (destination.origin !== window.location.origin) return;
      if (destination.hash && destination.pathname === window.location.pathname) return;
      if (!destination.pathname.startsWith('/collections/')) return;

      event.preventDefault();
      try {
        sessionStorage.setItem('smRealmOpening', '1');
      } catch (error) {
        /* Navigation should still continue even if storage is unavailable. */
      }
      playRealmExit(destination.href);
    }, true);
  }

  /* BFCache cleanup */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      document.querySelectorAll('.sm-realm-ov').forEach(function (el) {
        el.remove();
      });
    }
  });

  document.addEventListener('shopify:section:load', () => {
    initMagneticButtons();
    initProductCardGalleries();
    initProductGridTilt();
    initStampReveal();
    initDropArtifactSizing();
    initDropArtifactCentering();
    initDropArtifactGalleryNavigation();
  });

})();
